import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, ScanLine, Search, CloudOff, ClipboardCheck, CalendarClock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useApp } from '../store/useApp';
import { fmtInt, fmtHora } from '../lib/format';
import { Loading, EmptyState } from '../components/ui';
import { OnboardingGuia } from '../components/OnboardingGuia';
import { listarPendientes, leerCatalogo } from '../offline/db';
import { catalogoTidActual } from '../offline/catalogo';
import { integridadStock } from '../lib/logistica';
import { registerContract } from '../verify/contract';

const ESTADOS = ['libre', 'parcial', 'ocupada', 'bloqueada', 'cuarentena'];

// Aviso FEFO: palets cuya caducidad está a 30 días o menos (rojo ≤7, ámbar ≤30). Lee hueco.v_caducidades.
function CaducidadesProximas({ tid }) {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!tid || !navigator.onLine) { setRows([]); return; }
    let vivo = true;
    (async () => {
      const { data, error } = await supabase.from('v_caducidades')
        .select('lpn,estado,en_cuarentena,ubicacion,sku,articulo,lote,caducidad,cantidad,dias_restantes')
        .eq('tenant_id', tid).lte('dias_restantes', 30).order('dias_restantes', { ascending: true }).limit(20);
      if (vivo) setRows(error ? [] : (data || []));
    })();
    return () => { vivo = false; };
  }, [tid]);
  if (!rows || rows.length === 0) return null;
  const color = (d) => (Number(d) <= 7 ? 'var(--ocupada)' : '#e8a33d');
  return (
    <div className="card stack" data-testid="caducidades">
      <h2><CalendarClock size={16} /> {t('panel.caducidades')}</h2>
      <div style={{ overflowX: 'auto' }}>
        <table className="rtabla" data-testid="caducidades-tabla">
          <thead><tr>
            <th>{t('panel.cad_dias_col')}</th><th>{t('pedidos.articulo')}</th><th>{t('pedidos.lote')}</th>
            <th>{t('logistica.ubicacion')}</th><th>{t('pedidos.unidades')}</th><th>{t('logistica.matricula')}</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.lpn} data-testid={`caducidad-${r.lpn}`} style={{ cursor: 'pointer' }} onClick={() => nav(`/p/${r.lpn}`)}>
                <td data-label={t('panel.cad_dias_col')}>
                  <span className="badge" style={{ color: color(r.dias_restantes) }}>
                    {Number(r.dias_restantes) <= 0 ? t('panel.cad_caducado') : t('panel.cad_dias', { n: r.dias_restantes })}
                  </span>
                </td>
                <td data-label={t('pedidos.articulo')}>{r.articulo || r.sku || '—'}{r.en_cuarentena ? <span className="muted small"> · QC</span> : ''}</td>
                <td data-label={t('pedidos.lote')} className="mono-code">{r.lote || '—'}</td>
                <td data-label={t('logistica.ubicacion')} className="mono-code">{r.ubicacion || '—'}</td>
                <td data-label={t('pedidos.unidades')} className="mono-code">{fmtInt(r.cantidad, i18n.language)}</td>
                <td data-label={t('logistica.matricula')} className="mono-code">{r.lpn}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Panel() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const { tenantActivo, pendientes } = useApp();
  const rol = tenantActivo?.rol;
  const admin = ['owner', 'admin'].includes(rol);
  const [carga, setCarga] = useState(true);
  const [conteo, setConteo] = useState({});
  const [total, setTotal] = useState(0);
  const [movs, setMovs] = useState([]);
  const [rechazados, setRechazados] = useState(0);
  const [invPend, setInvPend] = useState(0);
  const [valorInv, setValorInv] = useState(null);
  const [bajoMin, setBajoMin] = useState(0);
  const [dataTid, setDataTid] = useState(null);
  const [integridad, setIntegridad] = useState(null); // {cuadra, descuadres, negativos}
  const [avisoFiscal, setAvisoFiscal] = useState(false); // plan de pago con datos fiscales incompletos
  const tid = tenantActivo?.tenant_id;

  useEffect(() => {
    if (!tid) return;
    // Borra de inmediato las cifras del tenant anterior: nunca mostrar números de otra empresa mientras
    // llegan los nuevos (aunque sea medio segundo). Se re-habilita cuando dataTid vuelve a igualar a tid.
    setConteo({}); setTotal(0); setMovs([]); setInvPend(0); setValorInv(null); setBajoMin(0); setIntegridad(null); setAvisoFiscal(false);
    (async () => {
      setCarga(true);
      // Pintado instantáneo desde caché offline (catálogo): conteo por estado + total salen de IndexedDB
      // para que el Panel aparezca YA en vez de un spinner. El servidor refina estos números abajo.
      if (admin) {
        const cache = catalogoTidActual() === tid ? await leerCatalogo('ubicaciones') : null;
        if (cache?.length) {
          const c = {}; ESTADOS.forEach((e) => { c[e] = 0; });
          cache.forEach((u) => { if (c[u.estado] != null) c[u.estado] += 1; });
          setConteo(c); setTotal(cache.length); setDataTid(tid); setCarga(false);
        }
      }
      const pend = await listarPendientes();
      setRechazados(pend.filter((p) => p.estado === 'rechazado').length);
      // Aviso de inventario inicial pendiente: SOLO del tenant ACTIVO (la RLS deja ver todos los tenants
      // del usuario, así que hay que filtrar o se mezclan empresas) y solo almacenes sin cerrar.
      if (navigator.onLine) {
        const { data: prog } = await supabase.from('v_inventario_progreso').select('inventario_cerrado_en, sin_comprobar').eq('tenant_id', tid);
        setInvPend((prog || []).filter((r) => !r.inventario_cerrado_en).reduce((a, r) => a + (Number(r.sin_comprobar) || 0), 0));
      }
      if (admin) {
        // En paralelo: 5 conteos por estado + total + últimos movimientos (evita N+1 secuencial).
        // Se usan conteos con head:true en vez de traer filas: Supabase corta a 1000 filas/respuesta
        // y hueco-demo tiene 1258 ubicaciones, así que un único select() daría cuentas erróneas.
        const [conteos, totRes, movRes] = await Promise.all([
          Promise.all(ESTADOS.map((e) =>
            supabase.from('ubicaciones').select('*', { count: 'exact', head: true })
              .eq('tenant_id', tid).eq('activo', true).eq('estado', e)
              .then(({ count }) => [e, count || 0]))),
          supabase.from('ubicaciones').select('*', { count: 'exact', head: true }).eq('tenant_id', tid).eq('activo', true),
          supabase.from('movimientos').select('id, tipo, cantidad, ts, ubicacion_destino_id').eq('tenant_id', tid).order('ts', { ascending: false }).limit(8),
        ]);
        setConteo(Object.fromEntries(conteos)); setTotal(totRes.count || 0); setMovs(movRes.data || []);
        // Valor total del inventario y artículos bajo mínimo (hueco.v_existencias).
        const { data: ex } = await supabase.from('v_existencias').select('valor,bajo_minimo').eq('tenant_id', tid);
        if (ex) {
          setValorInv(ex.reduce((a, r) => a + (Number(r.valor) || 0), 0));
          setBajoMin(ex.filter((r) => r.bajo_minimo).length);
        }
        // Bloque 3: integridad real (LIBRO vs tabla de stock). El sello ya no es decorativo.
        try { setIntegridad(await integridadStock(tid)); } catch (_) { setIntegridad(null); }
        // Aviso de facturación: plan de pago (taller/nave/grupo) FACTURABLE con datos fiscales incompletos.
        try {
          const [{ data: sub2 }, { data: fisc }, { data: ten }] = await Promise.all([
            supabase.rpc('estado_suscripcion', { p_tenant: tid }),
            supabase.rpc('datos_facturacion', { p_tenant: tid }),
            supabase.from('tenants').select('facturable').eq('id', tid).maybeSingle(),
          ]);
          const plan = String(sub2?.plan || sub2?.plan_codigo || sub2?.codigo_plan || '').toLowerCase();
          const dePago = ['taller', 'nave', 'grupo'].includes(plan);
          const esFacturable = ten?.facturable !== false;
          setAvisoFiscal(esFacturable && dePago && fisc && fisc.completo === false);
        } catch (_) { setAvisoFiscal(false); }
      }
      setDataTid(tid);
      setCarga(false);
    })();
  }, [tid, admin]);

  const suma = ESTADOS.reduce((a, e) => a + (conteo[e] || 0), 0);
  const cuadra = suma === total;

  useEffect(() => {
    if (!carga && admin) registerContract('PanelOcupacion', { campos: { total, suma }, reglas: { 'ocupacion-cuadra': suma === total } });
  }, [carga, admin, total, suma]);

  if (carga || dataTid !== tid) return <Loading />;

  const avisoInventario = invPend > 0 ? (
    <button className="banner row spread" onClick={() => nav('/inventario')} data-testid="alerta-inventario" style={{ cursor: 'pointer', textAlign: 'left' }}>
      <span className="row"><ClipboardCheck size={18} /> {t('panel.inventario_pendiente', { n: fmtInt(invPend, i18n.language) })}</span>
      <span className="muted small">{t('nav.inventario')} →</span>
    </button>
  ) : null;

  // --- Vista reducida para operario / lectura: solo acciones y su cola, sin ocupación agregada ---
  if (!admin) {
    return (
      <div className="stack" data-testid="panel">
        <div><h1>{t('panel.titulo')}</h1><p className="muted">{t('panel.operario_hint')}</p></div>
        {avisoInventario}
        <CaducidadesProximas tid={tid} />
        <div className="grid g2">
          <button className="card stack" onClick={() => nav('/escanear')} data-testid="accion-escanear"
            style={{ cursor: 'pointer', alignItems: 'flex-start', textAlign: 'left', border: '1px solid var(--line)' }}>
            <ScanLine size={30} className="dot" style={{ color: 'var(--cobalt)', width: 'auto', height: 'auto' }} />
            <h2>{t('nav.escanear')}</h2>
          </button>
          <button className="card stack" onClick={() => nav('/buscar')} data-testid="accion-buscar"
            style={{ cursor: 'pointer', alignItems: 'flex-start', textAlign: 'left', border: '1px solid var(--line)' }}>
            <Search size={30} className="dot" style={{ color: 'var(--cobalt)', width: 'auto', height: 'auto' }} />
            <h2>{t('nav.buscar')}</h2>
          </button>
        </div>
        <button className="card row spread" onClick={() => nav('/pendientes')} data-testid="operario-pendientes"
          style={{ cursor: 'pointer', textAlign: 'left' }}>
          <span className="row"><CloudOff size={18} /> {t('nav.pendientes')}</span>
          <span className="badge-num">{pendientes || 0}</span>
        </button>
      </div>
    );
  }

  const ocupadas = (conteo.ocupada || 0) + (conteo.parcial || 0);
  const pctRaw = total ? (ocupadas / total) * 100 : 0;
  const ocupadasPct = pctRaw === 0 ? '0' : pctRaw < 1 ? '<1' : pctRaw < 10 ? pctRaw.toFixed(1) : String(Math.round(pctRaw));

  return (
    <div className="stack" data-testid="panel"
      data-verify-unit="PanelOcupacion"
      data-verify-total={total}
      data-verify-suma={suma}
      data-verify-ocupacion-cuadra={String(cuadra)}>
      <div className="row spread wrap">
        <div><h1>{t('panel.titulo')}</h1><p className="muted small">{tenantActivo?.nombre}</p></div>
        <span className="badge" data-testid="cuadra-badge" style={{ color: integridad ? (integridad.negativos ? 'var(--ocupada)' : (integridad.cuadra ? 'var(--libre)' : '#e8a33d')) : undefined, cursor: integridad && !integridad.cuadra ? 'pointer' : 'default' }} onClick={() => { if (integridad && !integridad.cuadra) nav('/auditoria'); }}>
          {integridad
            ? (integridad.negativos
                ? <><AlertTriangle size={16} /> {t('panel.integridad_negativos', { n: integridad.negativos })}</>
                : integridad.cuadra
                  ? <><CheckCircle2 size={16} className="dot" style={{ color: 'var(--libre)', width: 'auto', height: 'auto' }} /> {t('panel.cuadra')}</>
                  : <><AlertTriangle size={16} /> {t('panel.integridad_descuadres', { n: integridad.descuadres ?? '?' })}</>)
            : <>{cuadra ? <CheckCircle2 size={16} className="dot" style={{ color: 'var(--libre)', width: 'auto', height: 'auto' }} /> : <AlertTriangle size={16} />} {cuadra ? t('panel.cuadra') : t('panel.no_cuadra')}</>}
        </span>
      </div>

      {avisoInventario}

      {invPend > 0 && (
        <button className="banner row spread" onClick={() => nav('/carga-inicial')} data-testid="alerta-carga-inicial" style={{ cursor: 'pointer', textAlign: 'left' }}>
          <span className="row"><ClipboardCheck size={18} /> {t('carga_inicial.panel_cta')}</span>
          <span className="muted small">{t('nav.carga_inicial')} →</span>
        </button>
      )}

        <button className="banner row spread" onClick={() => nav('/ajustes')} data-testid="alerta-fiscal"
          style={{ cursor: 'pointer', textAlign: 'left', background: '#fff4e0', borderColor: '#e8a33d', color: '#7a5410' }}>
          <span className="row"><AlertTriangle size={18} /> {t('fiscal.panel_incompletos')}</span>
          <span className="muted small">{t('nav.ajustes')} →</span>
        </button>
      )}

      <OnboardingGuia />

      <button className="card row spread" onClick={() => nav('/existencias')} data-testid="valor-inventario"
        style={{ cursor: 'pointer', textAlign: 'left', alignItems: 'center' }}>
        <div>
          <div className="k muted" style={{ textTransform: 'uppercase', letterSpacing: '.04em', fontSize: 12 }}>{t('panel.valor_inventario')}</div>
          <div style={{ fontSize: 40, fontWeight: 800, lineHeight: 1.1 }}>
            {valorInv == null ? '—' : new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }).format(valorInv)}
          </div>
        </div>
        <span className="muted small">{t('nav.existencias')} →</span>
      </button>

      {bajoMin > 0 && (
        <button className="banner row spread" onClick={() => nav('/existencias')} data-testid="alerta-bajo-minimo" style={{ cursor: 'pointer', textAlign: 'left' }}>
          <span className="row"><AlertTriangle size={18} /> {t('panel.bajo_minimo', { n: fmtInt(bajoMin, i18n.language) })}</span>
          <span className="muted small">{t('nav.existencias')} →</span>
        </button>
      )}

      <div className="grid g4">
        <div className="card stat" data-testid="stat-total"><div className="n">{fmtInt(total, i18n.language)}</div><div className="k">{t('comun.total')}</div></div>
        <div className="card stat" data-testid="stat-libres"><div className="n" style={{ color: 'var(--libre)' }}>{fmtInt(conteo.libre, i18n.language)}</div><div className="k">{t('panel.huecos_libres')}</div></div>
        <div className="card stat" data-testid="stat-ocupacion"><div className="n">{ocupadasPct}%</div><div className="k">{t('panel.ocupacion')}</div></div>
        <div className="card stat" data-testid="stat-rechazados"><div className="n" style={{ color: rechazados ? 'var(--ocupada)' : 'inherit' }}>{rechazados}</div><div className="k">{t('pendientes.rechazado')}</div></div>
      </div>

      <div className="grid g2">
        <div className="card stack">
          <h2>{t('panel.ocupacion')}</h2>
          {ESTADOS.map((e) => (
            <div className="row spread" key={e} data-testid={`ocup-${e}`}>
              <span className="badge"><span className={`dot ${e}`} />{t(`estados.${e}`)}</span>
              <strong>{fmtInt(conteo[e], i18n.language)}</strong>
            </div>
          ))}
        </div>

        <div className="card stack">
          <h2>{t('panel.ultimos_movimientos')}</h2>
          {movs.length === 0 ? <EmptyState msg={t('panel.sin_movimientos')} /> : (
            <table>
              <tbody>
                {movs.map((m) => (
                  <tr key={m.id} data-testid="mov-row" style={{ cursor: 'pointer' }} onClick={() => nav(`/movimiento/${m.id}`)}>
                    <td><span className="badge">{m.tipo}</span></td>
                    <td className="mono-code">{fmtInt(m.cantidad, i18n.language)}</td>
                    <td className="muted small">{fmtHora(m.ts, i18n.language)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <CaducidadesProximas tid={tid} />

      <div className="card stack">
        <h2>{t('panel.alertas')}</h2>
        {rechazados > 0 ? (
          <button className="banner row spread" onClick={() => nav('/pendientes')} data-testid="alerta-rechazados" style={{ cursor: 'pointer', textAlign: 'left' }}>
            <span className="row"><AlertTriangle size={18} /> {rechazados} × {t('pendientes.rechazado')}</span>
            <span className="muted small">{t('nav.pendientes')} →</span>
          </button>
        ) : <p className="muted" style={{ margin: 0 }}>{t('panel.sin_alertas')}</p>}
      </div>
    </div>
  );
}
